import React, { Component } from "react"
import axios from "axios"
import AxiosConfig from "../../../../../utilities/AxiosConfig"
import ReviewTypes from "./ReviewTypes/EditReviewTypes"
import ReviewsReferenceTypes from "../Reviews/ReviewsTypes/ReviewsReferenceTypes"
import reviewParams from "./ReviewParams"
import EditReview from "./EditReview/EditReview"
import { Accordion, Button, Card, Modal, OverlayTrigger, Popover, Tooltip } from "react-bootstrap"
import ReviewTimeline from "./ReviewTimeline/ReviewTimeline"
import ReviewTimelineTypes from "./ReviewTypes/ReviewTimelineTypes"
import snackBar from "../../../../../utilities/snackBar/SnackBar"
import moment from "moment"
import DeleteReview from "./DeleteReview/DeleteReview"
import reggie from "src/utilities/Reggie"
import ReviewControlBar from "./ReviewControlBar/ReviewControlsBar"
import Naggy from "src/utilities/naggy/Naggy"
import MsalAuth from "src/utilities/msalAuth"
import EmployeeDataTypes from "src/pages/hr/Types/EmployeeTypes"
import ReviewsTypes from "../Reviews/ReviewsTypes/ReviewsTypes"
import DocCarousel from "./DocCarousel/DocCarousel"
import ReviewSubmitNag from "./ReviewSubmitNag/ReviewSubmitNag"
import { createEmployeeReview, updateEmployeeReview } from "src/components/vault77/employeeReviews/api/employeeReview.api"
import { getVault77ErrorMessage } from "src/components/vault77/core/apiError"
import type { PayModel, PayModelStatus } from "./EditReview/PayAdjuster/PayAdjuster"
import "./Review.css"


interface ReviewProps {
    EmployeeReviewId: number
    contextUser: number | string
    manageParentState: Function
    updateStatus: Function
    reviewReference: ReviewsReferenceTypes | null
    deleteReview: Function
    loading: boolean
    employeeName: string
    doublePanelRef: any
    updateEmployee: Function
    detailedEmployeeData: EmployeeDataTypes | {}
    readOnly: boolean
    reviewListItem: ReviewsTypes
    getReviews: Function
    addOrUpdateNotification?: Function
}

interface ReviewState {
    review: ReviewTypes | any
    reviewCopy: ReviewTypes | {}
    eventType: string
    timelineItems: Array<ReviewTimelineTypes> | []
    loadingTimeline: boolean
    loadingReview: boolean
    modifiedPay: boolean
    showApprovals: null | boolean
    defaultDocUploaderOpen: boolean
    submitNag: boolean
    modelStatus: PayModelStatus
    // Proposed pay model + the paired Vault77 review id (created/updated on save,
    // applied to pay-rate history on HR approval).
    payModel: PayModel
    vault77ReviewId: number | null
}

class Review extends Component<ReviewProps, ReviewState> {

    timeFormat = "YYYY-MM-DD"
    requiredSubmitFields = ["ReviewDate", "ReviewedBy", "ProfitCenterId", "EmployeeReviewTypeId", "EmployeeReviewReasonId", "ReviewScore"]
    requiredSaveFields = ["ReviewedBy", "ReviewScore"]
    storageLocation = process.env.REACT_APP_REVIEWS_STORAGE_LOCATION || ""
    containerName = process.env.REACT_APP_REVIEWS_CONTAINER_NAME || ""
    fileStoreLocationId = process.env.REACT_APP_REVIEWS_IMAGE_FILE_STORE_LOCATION_ID || ""

    state = {
        review: JSON.parse(JSON.stringify(reviewParams)),
        reviewCopy: {},
        timelineItems: [],
        loadingReview: false,
        loadingTimeline: true,
        largeTimeline: false,
        confirmDelete: false,
        modifiedPay: false,
        eventType: "",
        showApprovals: false,
        defaultDocUploaderOpen: false,
        submitNag: false,
        // Start pessimistic so Save never flashes green before PayAdjuster has loaded and
        // proven the employee has an active pay rate. PayAdjuster pushes the real status on load:
        // oldPayBad stays true (Save disabled) until "Current" resolves to a PayRateHistory rate.
        modelStatus: {
            bad: true,
            oldPayBad: true,
            newPayBad: false
        },
        payModel: { OldPayRate: null, NewPayRate: null, EffectiveDate: "" },
        vault77ReviewId: null
    }

    componentDidMount() {
        this.getReview()
    }

    setEventType = (s: string) => {
        this.setState({ eventType: s })
    }

    handleChange = (event: { target: { value: string | null } }) => {
        let input = event.target.value
        let review = this.state.review
        let eventType = this.state.eventType
        let eventTypes = Object.keys(review)
        let temp: any = review
        if (input === "") {
            input = null
        } else if (input && eventType === "ReviewScore") {
            input = this.restrictReviewScore(input)

        }
        for (let i = 0; i < eventTypes.length; i++) {
            if (eventType === eventTypes[i]) {
                temp[eventTypes[i]] = input
                let param: object = { [eventTypes[i]]: input }
                this.appendParams([param])
            }
        }
    }

    restrictReviewScore = (input: string) => {
        if (parseFloat(input) > 5.0) {
            snackBar({ text: "Score must <= 5", type: "error", timeout: 5000 })
            return null
        } else if (parseFloat(input) < 0.0) {
            snackBar({ text: "Score must be >= 0", type: "error", timeout: 5000 })
            return null
        }
        return input
    }

    handleQuill = (event: any) => {
        let input = event
        if (reggie.filterTags(input) === "") {
            input = null
        }
        this.appendParams([{ Comments: input }])
    }

    appendParams = (params: Array<object>) => {
        if (!this.props.readOnly) {
            let review = this.state.review
            for (let i = 0; i < params.length; i++) {
                let key = Object.keys(params[i])
                let value = Object.values(params[i])
                if (value[0] !== "") {
                    review[key[0]] = value[0]
                } else {
                    review[key[0]] = null
                }
            }
            this.manageState([{ key: "review", value: review }])
        }
    }


    manageState = (keys: Array<{ key: string, value?: any }>) => {
        let state: any = this.state
        for (let i = 0; i < keys.length; i++) {
            if (Object.entries(keys[i]).length > 1) {
                state[keys[i].key] = keys[i].value
            } else {
                state[keys[i].key] = !state[keys[i].key]
            }
        }
        this.setState(state)
    }

    getReview = () => {
        this.manageState([{ key: "loadingReview", value: true }])
        axios.get(`${process.env.REACT_APP_API_URL}/api/employeereview/getEmployeeReview/${this.props.EmployeeReviewId}`, AxiosConfig.configValue("min"))
            .then((res) => {
                const possibleZeros = ["DeletedBy", "DisabledBy", "HRApprovedBy", "ManagerApprovedBy", "ReviewScore"]
                let review: ReviewTypes = res.data
                //console.log("Review get review", review)
                for (let i = 0; i < possibleZeros.length; i++) {
                    //@ts-ignore
                    if (review[possibleZeros[i]] === 0) {
                        //@ts-ignore
                        review[possibleZeros[i]] = null
                    }
                }
                // const showApprovals = typeof review.SubmittedDateUtc === "string"
                this.manageState(
                    [
                        { key: "review", value: review },
                        { key: "reviewCopy", value: JSON.parse(JSON.stringify(review)) },
                        { key: "loadingReview", value: false },
                        { key: "modifiedPay", value: false },
                        { key: "showApprovals", value: true },
                    ])
                this.getTimeline()
            })
            .catch(() => {
                snackBar({ type: "error", text: "Encountered an error", timeout: 3000 })
                this.manageState(
                    [
                        { key: "review", value: JSON.parse(JSON.stringify(reviewParams)) },
                        { key: "reviewCopy", value: {} },
                        { key: "loadingReview", value: false },
                        { key: "modifiedPay", value: false },
                        { key: "showApprovals", value: false },
                    ])
                this.props.manageParentState([{ key: "reviewDex", value: -1 }])
            })
    }


    updateReview = (submit?: boolean) => {
        this.manageState([{ key: "loadingReview", value: true }, { key: "submitNag", value: false }])
        let rb = this.state.review
        if (submit) {
            rb.SubmittedBy = this.props.contextUser
            rb.SubmittedDateUtc = moment().format()
        }
        axios.post(process.env.REACT_APP_API_URL + "/api/employeeReview/addOrUpdateEmployeeReview", rb, AxiosConfig.configValue("min"))
            .then(async () => {
                if (this.state.modifiedPay) {
                    await this.upsertVault77Review()
                    snackBar({ type: "success", text: "Review updated", timeout: 3000 })
                }
                this.props.getReviews()
            }).catch(err => {
                //console.log(err)
                // Surfaces the Vault77 error envelope (e.g. EMPLOYEE_PAYRATE_REQUIRED) when the
                // pay upsert is rejected; falls back to a generic message for anything else.
                snackBar({ type: "error", text: getVault77ErrorMessage(err, "Encountered an error"), timeout: 5000 })
            }).finally(() => {
                // Always re-fetch so legacy review state (and the loadingReview spinner set above)
                // never goes stale/stuck when the Vault77 pay upsert fails after the legacy save succeeds.
                this.getReview()
            })
    }

    // Persist the proposed pay to the paired Vault77 review (create on first save,
    // update thereafter). Linked to the legacy review via ExternalReviewId. The pay
    // is only pushed into PayRateHistory later, at HR approval (applyReview).
    upsertVault77Review = async (): Promise<number | null> => {
        const m = this.state.payModel
        const employeeId = this.state.review.EmployeeId
        const legacyReviewId = this.state.review.EmployeeReviewId
        if (!employeeId || !legacyReviewId) {
            return null
        }

        // OldPayRate (the employee's "Current" pay) is the server's to own — the API derives and
        // stamps it from the active PayRateHistory on save (a review is a proposal; PayRateHistory
        // is the source of truth), so we do NOT send it. We still read the current pay locally only
        // to default NewPayRate when none was entered (a no-op raise), mirroring the legacy fallback.
        const currentPay = m?.OldPayRate ?? null
        const newPay = m?.NewPayRate != null ? m.NewPayRate : currentPay
        const effDate = m?.EffectiveDate
            ? moment(m.EffectiveDate).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD")

        // ReviewContent is intentionally always blank here: this Vault77 review row exists only
        // to carry pay data for a legacy (FieldResponse2_1) review — the review text itself lives
        // on the legacy review, not here. Nothing reads ReviewContent off an ExternalReviewId-linked
        // row, so this is not a destructive write, just an unused field on this particular flow.
        if (this.state.vault77ReviewId) {
            await updateEmployeeReview({
                EmployeeReviewId: this.state.vault77ReviewId,
                EmployeeId: employeeId,
                ExternalReviewId: legacyReviewId,
                NewPayRate: newPay,
                EffectiveDate: effDate,
                ReviewContent: "",
            })
            return this.state.vault77ReviewId
        }

        const created = await createEmployeeReview({
            EmployeeId: employeeId,
            ExternalReviewId: legacyReviewId,
            NewPayRate: newPay,
            EffectiveDate: effDate,
            ReviewContent: "",
        })
        this.manageState([{ key: "vault77ReviewId", value: created.EmployeeReviewId }])
        return created.EmployeeReviewId
    }

    getTimeline = () => {
        this.setState({ loadingTimeline: true })
        axios.get(process.env.REACT_APP_API_URL + `/api/employeeReview/getReviewTimelineList/${this.state.review.EmployeeReviewId}/true `, AxiosConfig.configValue())
            .then(res => {
                this.setState({ timelineItems: res.data, loadingTimeline: false, defaultDocUploaderOpen: false })
            })
            .catch(err => {
                this.setState({ loadingTimeline: false })
                snackBar({ text: "Failed to fetch Timeline", type: "error", timeout: 4000 })
                //console.log(err)
            })
    }

    updateTimeline = (rb: ReviewTimelineTypes) => {
        this.setState({ loadingTimeline: true, })
        axios.post(process.env.REACT_APP_API_URL + "/api/employeeReview/addOrUpdateReviewTimeline", rb, AxiosConfig.configValue("min"))
            .then(res => {
                this.setState({ loadingTimeline: false })
                snackBar({ type: "success", text: "Timeline updated", timeout: 3000 })
                this.getTimeline()

            }).catch(err => {
                //console.log(err)
                this.setState({ loadingTimeline: false })
                snackBar({ type: "error", text: "Error updating timeline", timeout: 3000 })
            })
    }

    getReviewStatus = () => {
        let status = this.state.review.EmployeeReviewStatusId
        if (status === 1) {
            return "In Progress"
        } else {
            return "Completed"
        }
    }

    isDirty = () => {
        let a: any = JSON.parse(JSON.stringify(this.state.review))
        let b: any = this.state.reviewCopy
        a.Comments = reggie.filterTags(a.Comments)
        b.Comments = reggie.filterTags(b.Comments)
        let result: any = {};
        for (let i in b) {
            if (!a.hasOwnProperty(i) || b[i] !== a[i]) {
                result[i] = b[i];
            }
        }
        let temp = Object.keys(result)

        if (temp.length > 0 || this.state.modifiedPay) {
            return true
        }
        return false
    }

    checkRequired = (fields: Array<string>) => {
        const state = this.state
        let missing = []
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i]
            if (!state.review[field] && typeof state.review[field] !== "number") {
                missing.push(field)
            }
        }
        if(this.state.modelStatus.oldPayBad){
            missing.push("Employee must have an active pay rate (add one in the User Admin tab)")
        }
        if(this.state.modelStatus.newPayBad){
            missing.push("New pay must be >= 1")
        }
        return { missing: missing, allRequired: missing.length === 0 }
    }

    handleDiscard = () => {
        let ref = this.props.doublePanelRef
        ref.current.hideDetails()
    }

    render() {

        const props = this.props
        const state = this.state
        const dirtyForm = this.isDirty()
        const canSubmit = this.checkRequired(this.requiredSubmitFields)
        const canSave = this.checkRequired(this.requiredSaveFields)
        const readOnly = props.readOnly

        // //console.log("Review props: ", props)
        // // console.log("Review state: ", state)
        // //console.log("Review dirtyForm", dirtyForm)
        // //console.log("Review canSubmit", canSubmit)
        // console.log("model status", state.modelStatus)

        const MissingFieldPopover = (
            <Popover id="popover-basic">
                <Popover.Title as="h2">Please check the following fields:</Popover.Title>
                {canSubmit.missing.map((item: string, index: number) =>
                    <div className="uac-missing-info-tooltip-text" key={`miss-comp-${index}`}>
                        <p className="uac-missing-info-tooltip-text-item-number">{`${index + 1}. `}</p>
                        <p className="uac-missing-info-tooltip-text-item">{item.replace(/([A-Z])/g, ' $1').trim()}</p>
                    </div>
                )}
            </Popover>
        )

        const ReviewToolTip = (text: string) => (
            <Tooltip id="employee-review-tool-tip">
                {text}
            </Tooltip>
        )

        return (
            <div className="Review">
                <ReviewControlBar
                    loading={state.loadingReview}
                    manageParentState={this.manageState}
                    updateReview={this.updateReview}
                    isDirty={dirtyForm && canSave.allRequired}
                    missingFields={canSave.missing}
                    submitted={state.review.SubmittedBy}
                    readOnly={readOnly}
                />
                <div className="ReviewHeader" onClick={() => props.manageParentState([{ key: "reviewDex", value: -1 }])}>
                    <div className="ReviewHeaderColumnsLeft">
                        <div className="ReviewHeaderLeftColumn">
                            <p>{moment(state.review.ReviewDate).format("MM[/]DD[/]YYYY")}</p>
                        </div>
                        <div className="ReviewHeaderLeftColumn">
                            <p>{this.getReviewStatus()}</p>
                        </div>
                        <div className="ReviewHeaderLeftColumn">
                            {!props.reviewListItem.ManagerReview && <span className="material-icons">hourglass_empty</span>}
                            {props.reviewListItem.ManagerReview && <span id="review-header-left-column-done" className="material-icons">done</span>}
                            <p>Manager Approval</p>
                        </div>
                        <div className="ReviewHeaderLeftColumn">
                            {!props.reviewListItem.HRReview && <span className="material-icons">hourglass_empty</span>}
                            {props.reviewListItem.HRReview && <span id="review-header-left-column-done" className="material-icons">done</span>}
                            <p>Human Resources Approval</p>
                        </div>
                    </div>
                    <div className="ReviewHeaderColumnsRight">
                        <p className="ReviewHeaderLeftColumnInternalId">id: {props.reviewListItem.EmployeeReviewId}</p>
                        <div className="ReviewHeaderRightColumn">
                            <span className="material-icons">expand_less</span>
                        </div>
                    </div>
                </div>
                {this.state.review && <EditReview
                    review={state.review}
                    contextUserId={this.props.contextUser}
                    reviewReference={props.reviewReference}
                    appendParams={this.appendParams}
                    setEventType={this.setEventType}
                    handleChange={this.handleChange}
                    handleQuill={this.handleQuill}
                    timeFormat={this.timeFormat}
                    employeeName={props.employeeName}
                    submitStatus={canSubmit}
                    manageParentState={this.manageState}
                    updateStatus={this.props.updateStatus}
                    detailedEmployeeData={this.props.detailedEmployeeData}
                    readOnly={readOnly}
                    getReviews={this.props.getReviews}
                    showApprovals={state.showApprovals}
                    addOrUpdateNotification={this.props.addOrUpdateNotification}
                    loadingReview={state.loadingReview}
                    upsertVault77Review={this.upsertVault77Review}
                    refreshReview={this.getReview}
                />}
                {state.confirmDelete && <DeleteReview
                    updateReview={this.props.deleteReview}
                    manageParentState={this.manageState}
                    review={state.review}
                    contextUser={this.props.contextUser}
                    loading={state.loadingReview}
                />}
                <div className="EditReviewReviewFooter">
                    {(!canSubmit.allRequired && !state.review.SubmittedBy) && !readOnly &&
                        <Button className="EditREviewCantSumbitForReviewButton" disabled={true} variant="success" >
                            <OverlayTrigger delay={{ show: 0, hide: 0 }} placement="right" overlay={MissingFieldPopover}>
                                <div className="EditREviewSumbitForReviewButtonInnerHTML">
                                    <p>Submit For Review</p>
                                    <span className="material-icons">grading</span>
                                </div>
                            </OverlayTrigger>
                        </Button>}
                    {(canSubmit.allRequired && !state.review.SubmittedBy) && !readOnly &&
                        <Button className="EditREviewSumbitForReviewButton" variant="success" onClick={() => this.updateReview(true)}>
                            <div className="EditREviewSumbitForReviewButtonInnerHTML">
                                <p>Submit For Review</p>
                                <span className="material-icons">grading</span>
                            </div>
                        </Button>}
                    {state.review.SubmittedBy && <Button className="EditREviewSumbitForReviewButton" variant="secondary" disabled={true}>
                        <OverlayTrigger delay={{ show: 0, hide: 0 }} placement="right" overlay={ReviewToolTip("Review already submitted")}>
                            <div className="EditREviewSumbitForReviewButtonInnerHTML">
                                <p>Review has been submitted</p>
                                <span className="material-icons">grading</span>
                            </div>
                        </OverlayTrigger>
                    </Button>}
                    <div className="EditReviewQuickAttachBtn" id="b2ec0b2344ff0b84fd9841daf02e6b3167">
                        <OverlayTrigger delay={{ show: 0, hide: 0 }} placement="right" overlay={ReviewToolTip("Attach files to the timeline")}>
                            <span className="material-icons" onClick={() => this.manageState([{ key: "largeTimeline" }, { key: "defaultDocUploaderOpen", value: true }])}>attach_file</span>
                        </OverlayTrigger>
                    </div>
                    <div className="EditReviewDocCarousel" id="b2db323d95d4b429fccd11c1729408a">
                        {state.timelineItems.length > 0 && <DocCarousel
                            timelineItems={state.timelineItems}
                            manageParentState={this.manageState}
                        />}
                    </div>
                </div>
                {!state.largeTimeline && <Accordion defaultActiveKey={"0"} id="fc4474740989cdc751f74f651b4">
                    <Card>
                        <Accordion.Toggle as={Card.Header} eventKey="1">
                            <div className="ReviewTimelineAccordionHeader">
                                <p>Timeline</p>
                            </div>
                            <OverlayTrigger placement="left" overlay={ReviewToolTip("Open in larger view")}>
                                <div className="ReviewLargeViewBtn" onClick={() => this.manageState([{ key: "largeTimeline" }])}>
                                    <span className="material-icons">open_in_full</span>
                                </div>
                            </OverlayTrigger>
                        </Accordion.Toggle>
                        <Accordion.Collapse eventKey="1">
                            <div>
                                <ReviewTimeline
                                    EmployeeReviewTimelineId={state.review.EmployeeReviewId}
                                    timelineItems={state.timelineItems}
                                    loadingTimeline={state.loadingTimeline}
                                    timeFormat={this.timeFormat}
                                    toggleLarge={() => null}
                                    isModal={state.largeTimeline}
                                    contextUserId={props.contextUser}
                                    refreshTimeline={this.getTimeline}
                                    updateTimeLineItems={this.updateTimeline}
                                    readOnly={readOnly}
                                    defaultDocUploaderOpen={state.defaultDocUploaderOpen}
                                    manageParentState={this.manageState}
                                />
                            </div>
                        </Accordion.Collapse>
                    </Card>
                </Accordion>}
                {state.largeTimeline && <Modal id="review-timeline-large-view-modal"
                    show={true}
                    backdrop="static"
                    keyboard={true}
                    centered={true}
                    size="xl"
                >
                    <Modal.Header>
                        <div className="ReviewLargeViewModalHeader">
                            <p>Review Timeline</p>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="ReviewlargeViewModalBody">
                            <ReviewTimeline
                                EmployeeReviewTimelineId={state.review.EmployeeReviewId}
                                timelineItems={state.timelineItems}
                                loadingTimeline={state.loadingTimeline}
                                timeFormat={this.timeFormat}
                                toggleLarge={() => null}
                                isModal={state.largeTimeline}
                                contextUserId={props.contextUser}
                                refreshTimeline={this.getTimeline}
                                updateTimeLineItems={this.updateTimeline}
                                readOnly={readOnly}
                                defaultDocUploaderOpen={state.defaultDocUploaderOpen}
                                manageParentState={this.manageState}
                            />
                        </div>
                        <span id="review-timeline-modal-body-backdrop" className="material-icons">timeline</span>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="ReviewLargeViewModalFooter">
                            <Button id="review-large-view-exit-btn" variant="secondary" onClick={() => this.manageState([{ key: "largeTimeline" }])}>Back</Button>
                        </div>
                    </Modal.Footer>
                </Modal>}
                {state.submitNag && !state.review.SubmittedDateUtc && <ReviewSubmitNag
                    updateReview={this.updateReview}
                />}
                <Naggy
                    disabledElements={["employee-overview-close-btn-p-collapsed", "employee-overview-close-btn-p", "double-panel-table", "employee-filter-wrapper", "open-employee-search-options"]}
                    isDirty={dirtyForm}
                    toggleDirty={() => ({})}
                    discard={this.handleDiscard}
                    // message={this.discardMsg}
                    message={"Discard Changes?"}
                />
            </div >
        )
    }
}

export default Review